import { MessageUpdate } from '@sp/feature/stream-chat';
import {
  ChatMessageViewAttachment,
  isUploadable,
  sanitizeDataUrlInAttachment,
  UploadingStatus,
} from '@sp/util/chat-attachments';
import { assert, BooleanMap, isNotNullish, useDeepCompareMemoize } from '@sp/util/helpers';
import {
  ATTACHMENT_TYPE,
  ChatAttachment,
  ChatFormatMessage,
  ChatMessage,
  formatChatMessage,
  LinkAttachment,
  SelfplusExtraMessageFields,
} from '@sp/util/stream-chat';
import { isEmptyDoc, parseLinksFromDoc, parseMessageContent, parseUserMentionsFromDoc } from '@sp/util/text-editor';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAttachmentManager } from './use-attachment-manager';

export type MessageFormModel = ReturnType<typeof useMessageForm>;

export function mapAttachmentsToStream(attachments: readonly ChatMessageViewAttachment[]): ChatAttachment[] {
  return attachments.map(a => ({
    selfplusAttachment: sanitizeDataUrlInAttachment(a),
  }));
}

export function mapAttachmentsToClient(attachments: readonly ChatAttachment[]): ChatMessageViewAttachment[] {
  return attachments.map(a => a.selfplusAttachment).filter(isNotNullish);
}

const MAX_ATTACHMENT_COUNT = 10;

const updatableFieldsConfig: BooleanMap<Required<SelfplusExtraMessageFields>> = {
  selfplusCommunityPost: true,
};

const isExtraMessageField = (field: string): field is keyof SelfplusExtraMessageFields => {
  return field in updatableFieldsConfig;
};

export const useMessageForm = (data?: {
  defaultExtraFields?: SelfplusExtraMessageFields;
  defaultFields?: Partial<ChatFormatMessage>;
}) => {
  const [text, setText] = useState('');
  const [pinned, setPinned] = useState(false);
  const [editedMessage, setEditedMessage] = useState<ChatFormatMessage | null>(null);
  const [quotedMessage, setQuotedMessage] = useState<ChatFormatMessage | null>(null);
  const attachmentManager = useAttachmentManager();
  const [excludedUrls, setExcludedUrls] = useState<{ [url: string]: true | undefined }>({});

  const [meta, setMeta] = useState<ChatFormatMessage['selfplusMessageMeta']>({});
  const setMetaField = useCallback(
    <K extends keyof Required<ChatFormatMessage>['selfplusMessageMeta']>(
      field: K,
      value: Required<ChatFormatMessage>['selfplusMessageMeta'][K],
    ) => setMeta({ ...meta, [field]: value }),
    [meta],
  );

  const [extraFields, setExtraFields] = useState<Partial<SelfplusExtraMessageFields>>(data?.defaultExtraFields || {});
  const setExtraField = useCallback(
    <K extends keyof Required<SelfplusExtraMessageFields>>(field: K, value: Required<SelfplusExtraMessageFields>[K]) =>
      setExtraFields({ ...extraFields, [field]: value }),
    [extraFields],
  );

  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);

  const editedMessageMentionedUserIds = useDeepCompareMemoize(
    useMemo(() => editedMessage?.mentioned_users?.map(u => u.id) ?? [], [editedMessage]),
  );

  useEffect(() => {
    setMentionedUserIds(editedMessageMentionedUserIds);
  }, [editedMessageMentionedUserIds]);

  const reset = useCallback(() => {
    setQuotedMessage(null);
    setEditedMessage(null);
    setPinned(data?.defaultFields?.pinned ?? false);
    setMeta({});
    setExtraFields(data?.defaultExtraFields || {});
    setText('');
    setMentionedUserIds([]);
    attachmentManager.resetAttachments();
    setExcludedUrls({});
  }, [attachmentManager]);

  useEffect(() => {
    if (editedMessage == null) {
      reset();
    } else {
      setText(editedMessage.text ?? '');
      attachmentManager.resetAttachments(mapAttachmentsToClient(editedMessage.attachments ?? []));

      if (
        editedMessage.quoted_message_id != null &&
        editedMessage.quoted_message != null &&
        editedMessage.quoted_message.type !== 'deleted'
      ) {
        setQuotedMessage(formatChatMessage(editedMessage.quoted_message));
      } else {
        setQuotedMessage(null);
      }

      if (editedMessage.selfplusMessageMeta) {
        setMeta(editedMessage.selfplusMessageMeta);
      }

      if (isNotNullish(editedMessage.pinned)) {
        setPinned(editedMessage.pinned);
      }

      Object.keys(editedMessage).forEach(field => {
        if (isExtraMessageField(field)) {
          const val = editedMessage[field];
          if (isNotNullish(val)) {
            setExtraField(field, val);
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedMessage]);

  const resetQuote = useCallback(() => setQuotedMessage(null), []);

  // TODO[Dmitriy Teplov] move into attachments manager?
  const isAttachmentManagerReady = useMemo(() => {
    return !attachmentManager.attachments.some(
      attachment => isUploadable(attachment) && attachment.status !== UploadingStatus.ready,
    );
  }, [attachmentManager.attachments]);

  const syncLinkAttachments = useCallback(
    (text: string) => {
      if (text === '') {
        for (const attachment of attachmentManager.attachments.filter(
          attachment => attachment.type === ATTACHMENT_TYPE.link,
        )) {
          attachmentManager.removeAttachment(attachment);
        }

        return;
      }

      const links = parseLinksFromDoc(parseMessageContent(text)).filter(({ href }) => excludedUrls[href] !== true);

      // TODO[Dmitriy Teplov] move to attachments manager,
      //  can't be done while it's built on top of React Hooks
      //  because state only changes after render.
      let attachmentsCount = attachmentManager.attachments.length;

      // Batch deletion to avoid messing up with indexes.
      const attachmentsToDelete: LinkAttachment[] = [];

      // Remove deleted.
      for (let i = 0; i < attachmentManager.attachments.length; i++) {
        const attachment = attachmentManager.attachments[i];
        if (attachment.type === ATTACHMENT_TYPE.link && links.find(l => l.href === attachment.url) == null) {
          attachmentsToDelete.push(attachment);
        }
      }

      for (const deletedAttachment of attachmentsToDelete) {
        const isDeleted = attachmentManager.removeAttachment(deletedAttachment);
        if (isDeleted) {
          attachmentsCount--;
        }
      }

      // Add new.
      if (links.length > 0) {
        const linkAttachments = attachmentManager.attachments.filter(
          (a): a is LinkAttachment => a.type === ATTACHMENT_TYPE.link,
        );

        for (const link of links) {
          if (attachmentsCount >= MAX_ATTACHMENT_COUNT) {
            return;
          }

          const isNewLink = linkAttachments.find(a => a.url === link.href) == null;

          if (isNewLink) {
            attachmentManager.addLinkAttachment(link.href);
            attachmentsCount++;
          }
        }
      }
    },
    [attachmentManager, excludedUrls],
  );

  const syncMentionedUserIds = useCallback((text: string) => {
    if (text === '') {
      setMentionedUserIds([]);
    }

    const ids = parseUserMentionsFromDoc(parseMessageContent(text)).map(m => m.id);
    const uniqueIds = Array.from(new Set(ids));
    setMentionedUserIds(uniqueIds);
  }, []);

  useEffect(() => {
    syncLinkAttachments(text);
    syncMentionedUserIds(text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const excludeUrlFromAttachments = useCallback(
    (url: string) => setExcludedUrls(prev => ({ ...prev, [url]: true })),
    [],
  );

  const isEditing = editedMessage != null;
  const hasAttachments = attachmentManager.attachments.length > 0;
  const hasText = !isEmptyDoc(parseMessageContent(text));
  const canSave = hasAttachments ? isAttachmentManagerReady : hasText;

  const state = useMemo(
    () => ({
      text,
      pinned,
      mentionedUserIds,
      quotedMessage,
      editedMessage,
      hasAttachments,
      hasText,
      canSave,
      isEditing,
      meta,
      extraFields,
    }),
    [
      pinned,
      text,
      mentionedUserIds,
      quotedMessage,
      editedMessage,
      hasAttachments,
      hasText,
      canSave,
      isEditing,
      meta,
      extraFields,
    ],
  );

  return useMemo(
    () => ({
      state,
      reset,
      setText,
      edit: setEditedMessage,
      quote: setQuotedMessage,
      resetQuote,
      excludeUrlFromAttachments,
      attachmentManager,
      setMetaField,
      setExtraField,
    }),
    [attachmentManager, excludeUrlFromAttachments, reset, resetQuote, setExtraField, setMetaField, state],
  );
};

export function buildMessageUpdate(
  { editedMessage, quotedMessage, text, mentionedUserIds, meta, extraFields, pinned }: MessageFormModel['state'],
  attachments: ReadonlyArray<ChatMessageViewAttachment>,
): MessageUpdate {
  assert(editedMessage !== null);

  const update: Required<MessageUpdate['update']> = {
    set: {
      text,
      attachments: mapAttachmentsToStream(attachments),
      // Stream Chat types require mentioned_users to be an array of Users because of the inferred types,
      // but you only need to pass an array of ids.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      mentioned_users: mentionedUserIds as any,
      ...extraFields,
    },
    unset: [],
  };

  if (editedMessage.quoted_message_id != null && quotedMessage == null) {
    // FIXME[Dmitriy Teplov] update.unset.push('quoted_message_id') doesn't work.
    //  But setting quoted_message_id to an invalid id such as null does work.
    update.set.quoted_message_id = null as unknown as string;
  }
  if (isNotNullish(meta)) {
    update.set.selfplusMessageMeta = meta;
  }

  if (isNotNullish(pinned)) {
    update.set.pinned = pinned;
  }

  return {
    id: editedMessage.id,
    update,
  };
}

export function buildNewMessage(
  {
    editedMessage,
    text,
    quotedMessage,
    hasText,
    hasAttachments,
    mentionedUserIds,
    pinned,
    meta,
    extraFields,
  }: MessageFormModel['state'],
  attachments: ReadonlyArray<ChatMessageViewAttachment>,
): ChatMessage {
  assert(editedMessage == null);

  const newMessage: ChatMessage = extraFields;

  if (hasText) {
    newMessage.text = text;
  }

  if (isNotNullish(pinned)) {
    newMessage.pinned = pinned;
  }

  if (hasAttachments) {
    newMessage.attachments = mapAttachmentsToStream(attachments);
  }

  if (quotedMessage) {
    newMessage.quoted_message_id = quotedMessage.id;
  }

  if (mentionedUserIds.length > 0) {
    newMessage.mentioned_users = mentionedUserIds;
  }
  if (!isEmpty(meta)) {
    newMessage.selfplusMessageMeta = meta;
  }

  return newMessage;
}
